import React, { useEffect, useState } from 'react';
import { CameraVideo, FileEarmark, FileText } from 'react-bootstrap-icons';

function formatVideoDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return '';
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')} min`;
}

type Props = {
  hasVideo: boolean;
  hasDocument: boolean;
  hasDescription: boolean;
  wordCount: number;
  videoSource?: File | string | null;
  onViewVideo?: () => void;
  onViewDocument?: () => void;
  onViewDescription?: () => void;
};

type ResponseColProps = {
  active: boolean;
  label: string;
  title: string;
  onClick?: () => void;
  icon: React.ReactNode;
};

const ResponseCol: React.FC<ResponseColProps> = ({ active, label, title, onClick, icon }) => {
  const content = (
    <>
      <span
        className={`prompt-summary-response-col__icon-wrap${
          active ? ' prompt-summary-response-col__icon-wrap--active' : ''
        }`}
      >
        {icon}
      </span>
      <span
        className={`prompt-summary-response-col__value${
          active ? ' prompt-summary-response-col__value--active' : ''
        }`}
      >
        {label}
      </span>
    </>
  );

  if (active && onClick) {
    return (
      <button
        type="button"
        className="prompt-summary-response-col prompt-summary-response-col--clickable"
        onClick={onClick}
        title={title}
        aria-label={title}
      >
        {content}
      </button>
    );
  }

  return <div className="prompt-summary-response-col">{content}</div>;
};

const PromptSummaryResponseSummary: React.FC<Props> = ({
  hasVideo,
  hasDocument,
  hasDescription,
  wordCount,
  videoSource,
  onViewVideo,
  onViewDocument,
  onViewDescription,
}) => {
  const [videoLabel, setVideoLabel] = useState('');

  useEffect(() => {
    if (!hasVideo || !videoSource) {
      setVideoLabel('');
      return;
    }

    let cancelled = false;
    const video = document.createElement('video');
    const objectUrl =
      typeof videoSource === 'string' ? videoSource : URL.createObjectURL(videoSource);

    const cleanup = () => {
      if (typeof videoSource !== 'string') {
        URL.revokeObjectURL(objectUrl);
      }
    };

    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      if (!cancelled) {
        setVideoLabel(formatVideoDuration(video.duration));
      }
      cleanup();
    };
    video.onerror = () => {
      if (!cancelled) {
        setVideoLabel('');
      }
      cleanup();
    };
    video.src = objectUrl;

    return () => {
      cancelled = true;
      video.onloadedmetadata = null;
      video.onerror = null;
      cleanup();
    };
  }, [hasVideo, videoSource]);

  const videoText = hasVideo ? videoLabel || 'Video' : '—';
  const documentText = hasDocument ? 'Document' : '—';
  const descriptionText = hasDescription ? `${wordCount} words` : '—';

  return (
    <div className="prompt-summary-response-grid">
      <ResponseCol
        active={hasVideo}
        label={videoText}
        title="View video response"
        onClick={onViewVideo}
        icon={<CameraVideo className="prompt-summary-response-col__icon" aria-hidden />}
      />
      <ResponseCol
        active={hasDocument}
        label={documentText}
        title="View supporting document"
        onClick={onViewDocument}
        icon={<FileEarmark className="prompt-summary-response-col__icon" aria-hidden />}
      />
      <ResponseCol
        active={hasDescription}
        label={descriptionText}
        title="View description"
        onClick={onViewDescription}
        icon={<FileText className="prompt-summary-response-col__icon" aria-hidden />}
      />
    </div>
  );
};

export default PromptSummaryResponseSummary;
